import { createElement as h, Fragment, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { data, diaEHoraCurtos, numero, plural, pluralSemNumero, rotulo, sinal } from '../formato';
import { nomeDaAmostra } from '../NomeDaAmostra';
import type { DadosLab } from '../telas/PainelLaboratorio';
import type { Gravidade, GrupoDoResumo, Indicador, Pendencia } from './tipos';

/**
 * O adaptador do LabManager (onda 318, spec 06b §5, RN-256).
 *
 * O molde (`web/src/painel/`) não sabe de laboratório nenhum: recebe
 * `Pendencia[]` e `Indicador[]` prontos. Quem sabe é este arquivo, e ele só
 * REORGANIZA o que `GET /painel` e `GET /painel/laboratorio` já devolvem —
 * nenhuma conta nova, nenhum limiar novo (o PD-75 continua aberto). A
 * gravidade de um cartão vem do TIPO da situação (a tabela 5.1), nunca de
 * quantos dias ela está parada.
 *
 * Três peças:
 *
 *  · `TEXTOS_DO_PAINEL` — o que cada cartão diz: o título (em até oito
 *    palavras, no singular e no plural), a consequência numa linha e a
 *    explicação do «i». A explicação é o texto que o painel antigo escrevia
 *    no meio da tela (o `porque` de cada trava, o `quem` de cada espera, a
 *    `consequencia` de cada vencimento, os parágrafos de regra e os avisos
 *    da medição e do prazo), movido para a um toque — sem perder conteúdo;
 *  · `LINHAS` — a tabela 5.1: gravidade, área, grupo do resumo, ação e a
 *    permissão de quem resolve (o «Comigo»);
 *  · `pendenciasDoLaboratorio` e `indicadoresDoLaboratorio` — as duas
 *    traduções.
 *
 * Uma chave nova da API sem texto aqui continua aparecendo, pelo rótulo da
 * chave e sem «i» (o 30.1 cobra que toda chave tenha texto; o 305.25 confere
 * que a que ainda não tem não some).
 */

// ── O que `GET /painel` devolve ──────────────────────────────────────

interface Travado {
  chave: string; quantos: number; exemplo: string | null;
  /** 303 (RN-248): nas travas de amostra, o nome que o cliente deu. */
  exemplo_identificacao?: string | null;
}
interface Esperando { chave: string; quantos: number; dias: number | null }
interface Vencendo { chave: string; vencidos: number; a_vencer: number; proximo: string | null }
interface Prazo {
  atrasadas: number; pior_atraso: number; vencendo: number;
  sem_prazo: number; abertas: number; entregues: number; no_prazo: number;
}
interface Preso {
  pendencia: string; texto: string; ensaios: number;
  metodos: number; ordens: number;
  dias_do_mais_parado: number | null; exemplo: string | null;
}
interface Medicao {
  itens_nao_medidos: number; os_com_nao_medido: number;
  itens_com_saldo: number; itens_concluidos: number;
  itens_excedentes: number; os_sem_escopo: number;
  em_elaboracao: number; devolvidas: number;
  aguardando_conferencia: number; aguardando_aprovacao: number;
  aprovadas_sem_emitir: number; contestadas: number; emitidas: number;
}
interface Atrasado {
  os_numero: string; ensaios: number; pior_atraso: number;
  exemplo: string | null;
  /** 303 (RN-248): o nome que o cliente deu à amostra do exemplo. */
  exemplo_identificacao?: string | null;
  nem_comecaram: number;
}
/** Um ensaio com cápsula esquecida na estufa (onda 305, RN-250). */
interface Esquecido {
  ensaio_id: string; tipo: string; sigla: string; amostra_codigo: string;
  amostra_identificacao?: string | null;
  os_numero: string | null; capsulas: number; desde: string; lista: string;
}

export interface DadosDoPainel {
  travado: Travado[];
  atrasados: Atrasado[];
  preso: Preso[];
  esperando: Esperando[];
  acervo: {
    porStatus: { status: string; quantos: number }[];
    entradas: { dia: string; quantos: number }[];
    inicios: { dia: string; quantos: number }[];
  };
  vencendo: Vencendo[];
  prazo: Prazo;
  medicao: Medicao | null;
  /** As cápsulas na estufa há mais que o limiar (onda 305). */
  estufa?: { horas: number; capsulas: number; ensaios: Esquecido[] };
}

/** Uma semana da tendência (M4, `GET /painel/laboratorio/semanas`). */
export interface Semana {
  de: string; ate: string;
  entraram: number; sairam: number;
  mediana_laudo: number | null;
  na_meta: number; terminados: number;
}

// ── Os textos ────────────────────────────────────────────────────────

/**
 * O teto da lista das OS com ensaio que passou da data: o `LIMIT 12` de
 * `atrasados()` em `api/src/painel/painel.controller.ts`. A consulta não
 * muda (spec 06b: nenhuma consulta existente muda); quando a lista vem
 * cheia, o cartão diz que pode haver mais em vez de contar «12» calado
 * (Ruling R23). Mudou o LIMIT lá, muda aqui.
 */
export const TETO_DOS_ATRASADOS = 12;

interface TextoDoCartao {
  /** O que é, em até oito palavras: [um, vários]. */
  titulo: readonly [string, string];
  /** Só nos vencimentos: o título do cartão do que vence em 30 dias. */
  tituloAVencer?: readonly [string, string];
  /** A consequência, numa linha. */
  consequencia: string;
  /** O «i»: um parágrafo por item. `{horas}` é trocado pelo ajuste da estufa. */
  explicacao: readonly string[];
  /** Os parágrafos a mais quando a lista da API bate no teto (`{teto}` é o teto). */
  noTeto?: readonly string[];
}

/* As frases que abriam cada bloco do painel antigo: agora abrem o «i» dos
   cartões do bloco, para dizer por que o cartão conta naquele número. */
const TRAVADO = 'Travado: parado por um motivo que ninguém percebeu.';
const ESPERANDO = 'Esperando alguém: não depende de trabalho novo — só de alguém olhar.';
const MAIS_PARADO = 'Mais parado, e não a média: a média esconde justamente o registro '
  + 'esquecido, que é o único que este cartão existe para mostrar. Quantos dias são '
  + 'demais é decisão da Testesolo e ainda não foi tomada — por isso a cor do cartão '
  + 'vem do tipo da situação, nunca dos dias parados.';
const VENCE = 'Vence em 30 dias: o que vira trava se ninguém olhar agora.';
const VENCIDO_E_A_VENCER = 'Vencido e a vencer aparecem separados de propósito: somá-los '
  + 'esconderia o que já está impedindo ensaio hoje. Trinta dias é janela de leitura, '
  + 'não critério técnico — é o intervalo em que ainda dá para agendar uma calibração '
  + 'externa sem parar a bancada.';
const SEM_PERCENTUAL = 'Não há «percentual físico executado» geral de propósito: ele exigiria '
  + 'somar ensaio com metro perfurado e diária, e o número sairia sem significar nada. O '
  + 'percentual está por item, na ficha da OS e no boletim.';

export const TEXTOS_DO_PAINEL: Record<string, TextoDoCartao> = {
  // ── Travado ──
  'travado.ensaio_sem_verificador': {
    titulo: ['ensaio sem verificador habilitado', 'ensaios sem verificador habilitado'],
    consequencia: 'Ninguém pode verificar, e o laudo não sai.',
    explicacao: [TRAVADO,
      'Quem executou não pode verificar o próprio trabalho, e não há outra pessoa '
      + 'autorizada no método. O ensaio espera alguém que não existe — e sem este aviso '
      + 'isso só apareceria na hora de gravar.'],
  },
  'travado.equipamento_impedido': {
    titulo: ['equipamento não pode entrar em ensaio', 'equipamentos não podem entrar em ensaio'],
    consequencia: 'Fica fora das listas de seleção de ensaio.',
    explicacao: [TRAVADO,
      'Calibração externa vencida, calibração interna não conforme, baixa registrada ou um sensor de dentro '
      + 'impedido. Enquanto estiver assim, ele não é oferecido em nenhum ensaio.'],
  },
  'travado.constante_nao_publicada': {
    titulo: ['constante medida esperando assinatura', 'constantes medidas esperando assinatura'],
    consequencia: 'O cálculo do ensaio procura e não acha.',
    explicacao: [TRAVADO,
      'A calibração interna está feita e o certificado interno assinado no papel — só falta aprová-lo '
      + 'no sistema, que é o gesto que publica. Enquanto isso, o cálculo do ensaio procura a '
      + 'tara, o volume ou a altura e não acha.'],
  },
  'travado.item_sem_constante': {
    titulo: ['item calibrado sem constante vigente', 'itens calibrados sem constante vigente'],
    consequencia: 'O cálculo não encontra a tara, o volume ou a altura.',
    explicacao: [TRAVADO,
      'Cápsula sem tara, cilindro sem volume, anel sem altura. O equipamento aparece na '
      + 'lista e o cálculo não encontra o número — e a conta que não acha a constante é a '
      + 'conta que alguém completa de cabeça.'],
  },
  'travado.amostra_sem_os': {
    titulo: ['amostra recebida sem OS', 'amostras recebidas sem OS'],
    consequencia: 'Nenhum ensaio pode começar.',
    explicacao: [TRAVADO,
      'Estão rastreadas e guardadas, e mesmo assim nenhum ensaio pode começar. É a trava '
      + 'que não dói até alguém cobrar o laudo.'],
  },
  'travado.amostra_a_guardar': {
    titulo: ['amostra sem lugar registrado', 'amostras sem lugar registrado'],
    consequencia: 'Saiu do ensaio e ninguém registrou onde está.',
    explicacao: [TRAVADO,
      'O sistema sabe sozinho quando o ensaio começa; não sabe que alguém devolveu a amostra '
      + 'à prateleira. É o único toque que a custódia pede — e sem ele o inventário do '
      + 'laboratório não fecha.'],
  },
  'travado.os_incompleta': {
    titulo: ['OS sem prazo ou sem responsável técnico', 'OS sem prazo ou sem responsável técnico'],
    consequencia: 'Sem prazo, nunca aparece como atrasada.',
    explicacao: [TRAVADO,
      'Sem prazo, a OS nunca aparece como atrasada e ninguém cobra. Sem responsável técnico, '
      + 'o laudo não tem quem assine. Editar a OS resolve — e pede o motivo.'],
  },
  'travado.recebimento_sem_foto': {
    titulo: ['recebimento sem o mínimo de fotos', 'recebimentos sem o mínimo de fotos'],
    consequencia: 'Trava a emissão do relatório de recebimento.',
    explicacao: [TRAVADO,
      'O roteiro fotográfico não foi completado em alguma amostra, e isso trava a emissão do '
      + 'relatório de recebimento.'],
  },

  // ── Esperando alguém ──
  'esperando.ensaio_verificacao': {
    titulo: ['ensaio esperando verificação', 'ensaios esperando verificação'],
    consequencia: 'Depende de quem verifica o método.',
    explicacao: [ESPERANDO, 'Espera: quem verifica o método.', MAIS_PARADO],
  },
  'esperando.ensaio_devolvido': {
    titulo: ['ensaio devolvido para correção', 'ensaios devolvidos para correção'],
    consequencia: 'Voltou para quem executou corrigir.',
    explicacao: [ESPERANDO, 'Espera: quem executou.', MAIS_PARADO],
  },
  'esperando.ensaio_processamento': {
    titulo: ['ensaio lançado e sem processar', 'ensaios lançados e sem processar'],
    consequencia: 'Os dados estão lançados; falta processar.',
    explicacao: [ESPERANDO, 'Espera: quem processa.', MAIS_PARADO],
  },
  'esperando.analise_sem_aprovacao': {
    titulo: ['análise de certificado sem aprovação', 'análises de certificado sem aprovação'],
    consequencia: 'O certificado fica sem carimbo até a aprovação.',
    explicacao: [ESPERANDO,
      'Espera: quem aprova certificado — e não pode ser quem analisou.', MAIS_PARADO],
  },
  'esperando.laudo_sem_entrega': {
    titulo: ['laudo emitido e não entregue', 'laudos emitidos e não entregues'],
    consequencia: 'O prazo de guarda só conta da entrega.',
    explicacao: [ESPERANDO,
      'Espera: quem disponibiliza pelo portal — é daí que o prazo de guarda conta.', MAIS_PARADO],
  },
  'esperando.amostra_para_descarte': {
    titulo: ['amostra com guarda vencida', 'amostras com guarda vencida'],
    consequencia: 'Esperam a baixa no descarte.',
    explicacao: [ESPERANDO,
      'Espera: quem descarta — a fila está em «Descarte de amostras».', MAIS_PARADO],
  },

  // ── Vence em 30 dias ──
  'vencendo.calibracao': {
    titulo: ['calibração externa vencida', 'calibrações externas vencidas'],
    tituloAVencer: ['calibração externa vence em 30 dias', 'calibrações externas vencem em 30 dias'],
    consequencia: 'Vencida, o equipamento sai das listas de seleção de ensaio.',
    explicacao: [VENCE, VENCIDO_E_A_VENCER],
  },
  'vencendo.verificacao_interna': {
    titulo: ['calibração interna vencida', 'calibrações internas vencidas'],
    tituloAVencer: ['calibração interna vence em 30 dias', 'calibrações internas vencem em 30 dias'],
    consequencia: 'Vencida, o equipamento deixa de ter valor calibrado vigente.',
    explicacao: [VENCE, VENCIDO_E_A_VENCER],
  },
  'vencendo.competencia': {
    titulo: ['competência de método vencida', 'competências de método vencidas'],
    tituloAVencer: ['competência de método vence em 30 dias', 'competências de método vencem em 30 dias'],
    consequencia: 'Vencida, a pessoa deixa de contar como executante ou verificador.',
    explicacao: [VENCE,
      'Vencida, a pessoa deixa de contar como executante ou verificador — e o ensaio para '
      + 'sem ninguém entender por quê.',
      VENCIDO_E_A_VENCER],
  },

  // ── O que não é bloco de chaves ──
  estufa: {
    titulo: ['cápsula esquecida na estufa', 'cápsulas esquecidas na estufa'],
    consequencia: 'Sem a pesagem seca, o ensaio delas não fecha.',
    explicacao: [
      'A cápsula fica na estufa até ser pesada seca ou ter a linha descartada na ficha — sem '
      + 'prazo, e o ensaio dela continua travado até lá. As {horas} h são o ajuste «Alerta da '
      + 'cápsula esquecida na estufa» (engrenagem › Ajustes do sistema).'],
  },
  preso: {
    titulo: ['ensaio preso', 'ensaios presos'],
    consequencia: 'Impede o laudo de sair.',
    explicacao: [
      'Preso em quê: o que impede o laudo de sair, somado por motivo.',
      'O número de OS é o que muda a providência. Vinte ensaios sem cv espalhados por nove '
      + 'OS são fila de interpretação, e o que falta é tempo de responsável técnico. Onze '
      + 'cápsulas sem tara numa OS só não são fila: é uma calibração interna vencida parando onze '
      + 'ensaios de uma vez, e some com meia hora na balança. Os dois números têm o mesmo '
      + 'tamanho e pedem coisas diferentes.',
      'Só entram ensaios a caminho da verificação. O recém-programado tem todas as pendências '
      + 'da ficha, e contá-lo faria este número exagerar.'],
  },
  atrasados: {
    titulo: ['OS com ensaio que passou da data', 'OS com ensaios que passaram da data'],
    noTeto: [
      'A lista que a API manda traz no máximo as {teto} OS com pior atraso: quando o cartão '
      + 'mostra {teto}, pode haver mais OS com ensaio que passou da data — o número é um piso, '
      + 'e não o total.'],
    consequencia: 'Ensaios não entregues até a data programada.',
    explicacao: [
      'Passou da data: ensaios que não foram entregues até a data marcada na programação.',
      'A data é a que foi marcada na programação, e não um prazo que o sistema escolheu. '
      + 'Reprogramar exige dizer por quê — é isso que responde, depois, por que a OS atrasou.'],
  },
  'prazo.atrasadas': {
    titulo: ['OS atrasada no prazo contratual', 'OS atrasadas no prazo contratual'],
    consequencia: 'O compromisso com o cliente já venceu.',
    explicacao: [
      'O atraso vem com o pior, e não só com a contagem: três atrasadas com a pior de dois '
      + 'dias e três com a pior de dois meses são situações diferentes.',
      'OS sem prazo contratual nunca vão aparecer como atrasadas — não há data para comparar. '
      + 'Enquanto houver OS assim, o número de atrasos conta só parte do laboratório.',
      '«Vencem em até 7 dias» é janela de leitura, e não critério contratual da Testesolo.',
      'O percentual entregue no prazo só aparece quando houver OS concluída com data de fim '
      + 'realizada preenchida. Deduzir a entrega pela mudança de status seria transformar '
      + 'registro administrativo em evidência de entrega.'],
  },
  'medicao.itens_nao_medidos': {
    titulo: ['item executado e não medido', 'itens executados e não medidos'],
    consequencia: 'Trabalho feito que ainda não entrou em boletim.',
    explicacao: [
      'O que já foi feito e ainda não foi cobrado: um ensaio aprovado que ninguém mediu não '
      + 'vira alerta em lugar nenhum — ele simplesmente continua lá.',
      SEM_PERCENTUAL],
  },
  'medicao.boletins_parados': {
    titulo: ['boletim de medição parado', 'boletins de medição parados'],
    consequencia: 'Esperam conferência, aprovação ou emissão.',
    explicacao: [
      'Os aprovados e não emitidos já passaram pelas três assinaturas e ainda não viraram '
      + 'documento. Enquanto não forem emitidos, não há PDF com hash para mandar ao cliente.',
      SEM_PERCENTUAL],
  },
  'medicao.contestadas': {
    titulo: ['boletim contestado pelo cliente', 'boletins contestados pelo cliente'],
    consequencia: 'Contestação não se resolve sozinha.',
    explicacao: [
      'Contestação não se resolve sozinha: ou o boletim é substituído por uma revisão, ou a '
      + 'divergência é esclarecida e registrada.'],
  },
  'medicao.itens_excedentes': {
    titulo: ['item medido acima do contratado', 'itens medidos acima do contratado'],
    consequencia: 'Cada um tem justificativa ou aditivo registrado.',
    explicacao: [
      'Cada um deles tem justificativa ou aditivo registrado — o sistema não deixa passar sem. '
      + 'Vale conferir se o aditivo correspondente existe também do lado do cliente.'],
  },
  'medicao.os_sem_escopo': {
    titulo: ['OS com serviço executado e sem escopo', 'OS com serviço executado e sem escopo'],
    consequencia: 'Escapa de todos os números da medição.',
    explicacao: [
      'Elas escapam de todos os números da medição: sem o previsto não há contra o que '
      + 'comparar, então não aparecem com saldo, nem com excedente, nem como não medidas. '
      + 'Enquanto estiverem assim, a medição conta só parte da casa.'],
  },
};

/**
 * A frase que acompanha o «Nada travado e nada esperando alguém.» (o aviso
 * do dia tranquilo do painel antigo, dito para o molde).
 */
export const COMPLEMENTO_DO_DIA_TRANQUILO = 'Os cartões somem quando estão vazios — de '
  + 'propósito: painel cheio de zeros ensina a não olhar.';

// ── A tabela 5.1: gravidade, área, resumo, ação e quem resolve ───────

interface Linha {
  gravidade: Gravidade;
  area: string;
  grupo?: GrupoDoResumo;
  acao: { texto: string; para: string };
  /** A permissão de quem resolve; `'a|b'` quando qualquer uma das duas basta. */
  permissao: string;
}

const LAB = 'Laboratório';
const AMOSTRAS = 'Amostras';
const EQUIP = 'Equipamentos';
const OS_E_PRAZO = 'OS e prazo';
const QUALIDADE = 'Qualidade';
const MEDICAO = 'Medição';

/** A ordem dos chips de área (os cartões saem do adaptador nesta ordem). */
const ORDEM_DAS_AREAS = [LAB, AMOSTRAS, EQUIP, OS_E_PRAZO, QUALIDADE, MEDICAO];

const VER_A_FILA = { texto: 'Ver a fila', para: '/fila' };
const VER_OS_CERTIFICADOS = { texto: 'Ver os certificados', para: '/equipamentos/verificacoes' };
const VER_AS_AMOSTRAS = { texto: 'Ver as amostras', para: '/amostras' };
const ABRIR_A_MEDICAO = { texto: 'Abrir a medição', para: '/medicoes' };

const LINHAS: Record<string, Linha> = {
  'travado.ensaio_sem_verificador': { gravidade: 'trava', area: LAB, grupo: 'travado', acao: VER_A_FILA, permissao: 'competencia.conceder' },
  'travado.equipamento_impedido': { gravidade: 'trava', area: EQUIP, grupo: 'travado', acao: { texto: 'Ver o parque', para: '/equipamentos' }, permissao: 'equipamento.editar' },
  'travado.constante_nao_publicada': { gravidade: 'trava', area: EQUIP, grupo: 'travado', acao: VER_OS_CERTIFICADOS, permissao: 'equipamento.aprovar_certificado' },
  'travado.item_sem_constante': { gravidade: 'trava', area: EQUIP, grupo: 'travado', acao: { texto: 'Ver as pendências', para: '/equipamentos/pendencias' }, permissao: 'equipamento.verificar' },
  'travado.amostra_sem_os': { gravidade: 'atencao', area: AMOSTRAS, grupo: 'travado', acao: VER_AS_AMOSTRAS, permissao: 'os.editar' },
  'travado.amostra_a_guardar': { gravidade: 'atencao', area: AMOSTRAS, grupo: 'travado', acao: { texto: 'Ver as amostras', para: '/amostras?estado=a_guardar' }, permissao: 'amostra.movimentar' },
  'travado.os_incompleta': { gravidade: 'atencao', area: OS_E_PRAZO, grupo: 'travado', acao: { texto: 'Completar as OS', para: '/os?incompletas=true' }, permissao: 'os.editar' },
  'travado.recebimento_sem_foto': { gravidade: 'atencao', area: AMOSTRAS, grupo: 'travado', acao: { texto: 'Ver os recebimentos', para: '/recebimentos' }, permissao: 'amostra.receber' },
  'esperando.ensaio_verificacao': { gravidade: 'atencao', area: LAB, grupo: 'esperando', acao: { texto: 'Verificar', para: '/fila' }, permissao: 'ensaio.verificar' },
  'esperando.ensaio_devolvido': { gravidade: 'atencao', area: LAB, grupo: 'esperando', acao: VER_A_FILA, permissao: 'ensaio.lancar' },
  'esperando.ensaio_processamento': { gravidade: 'atencao', area: LAB, grupo: 'esperando', acao: VER_A_FILA, permissao: 'ensaio.processar' },
  'esperando.analise_sem_aprovacao': { gravidade: 'atencao', area: EQUIP, grupo: 'esperando', acao: VER_OS_CERTIFICADOS, permissao: 'equipamento.aprovar_certificado' },
  'esperando.laudo_sem_entrega': { gravidade: 'atencao', area: OS_E_PRAZO, grupo: 'esperando', acao: { texto: 'Ver os relatórios', para: '/relatorios' }, permissao: 'relatorio.disponibilizar' },
  'esperando.amostra_para_descarte': { gravidade: 'saber', area: AMOSTRAS, grupo: 'esperando', acao: { texto: 'Ver o descarte', para: '/descarte' }, permissao: 'amostra.descartar' },
  estufa: { gravidade: 'atencao', area: LAB, grupo: 'esperando', acao: { texto: 'Abrir a Estufa', para: '/estufa?alerta=1' }, permissao: 'ensaio.lancar' },
  preso: { gravidade: 'atencao', area: LAB, grupo: 'esperando', acao: VER_A_FILA, permissao: 'ensaio.interpretar' },
  atrasados: { gravidade: 'atencao', area: OS_E_PRAZO, grupo: 'atrasado', acao: { texto: 'Ver as OS', para: '/os?atrasadas=true' }, permissao: 'os.programar' },
  'prazo.atrasadas': { gravidade: 'atencao', area: OS_E_PRAZO, grupo: 'atrasado', acao: { texto: 'Ver as OS', para: '/os?atrasadas=true' }, permissao: 'os.editar' },
  /* Os vencimentos não tinham ação no painel antigo: a tela de cada um é a
     que resolve. A gravidade vem de vencido (atenção) ou a vencer (para
     saber), não daqui. */
  'vencendo.calibracao': { gravidade: 'atencao', area: EQUIP, grupo: 'vence', acao: { texto: 'Ver as pendências', para: '/equipamentos/pendencias' }, permissao: 'equipamento.editar' },
  'vencendo.verificacao_interna': { gravidade: 'atencao', area: EQUIP, grupo: 'vence', acao: { texto: 'Ver as calibrações internas', para: '/equipamentos/verificacoes' }, permissao: 'equipamento.verificar' },
  'vencendo.competencia': { gravidade: 'atencao', area: QUALIDADE, grupo: 'vence', acao: { texto: 'Ver as competências', para: '/competencias' }, permissao: 'competencia.conceder' },
  'medicao.itens_nao_medidos': { gravidade: 'saber', area: MEDICAO, acao: ABRIR_A_MEDICAO, permissao: 'medicao.elaborar' },
  'medicao.boletins_parados': { gravidade: 'atencao', area: MEDICAO, grupo: 'esperando', acao: ABRIR_A_MEDICAO, permissao: 'medicao.conferir|medicao.aprovar' },
  'medicao.contestadas': { gravidade: 'atencao', area: MEDICAO, acao: ABRIR_A_MEDICAO, permissao: 'medicao.elaborar' },
  'medicao.itens_excedentes': { gravidade: 'saber', area: MEDICAO, acao: ABRIR_A_MEDICAO, permissao: 'medicao.ajustar' },
  'medicao.os_sem_escopo': { gravidade: 'atencao', area: MEDICAO, acao: { texto: 'Ver as OS', para: '/os' }, permissao: 'os.editar' },
};

/** A chave que este arquivo ainda não conhece: aparece, pelo rótulo, sem «i». */
const DESCONHECIDA: Record<'travado' | 'esperando' | 'vencendo', Linha> = {
  travado: { gravidade: 'trava', area: 'Outros', grupo: 'travado', acao: VER_A_FILA, permissao: '' },
  esperando: { gravidade: 'atencao', area: 'Outros', grupo: 'esperando', acao: VER_A_FILA, permissao: '' },
  vencendo: { gravidade: 'atencao', area: 'Outros', grupo: 'vence', acao: { texto: 'Ver o parque', para: '/equipamentos' }, permissao: '' },
};

// ── Ajudantes ────────────────────────────────────────────────────────

const n = (v: unknown) => Number(v ?? 0);
/**
 * 1,5 — vírgula decimal e no máximo uma casa. Os números, o sinal e as
 * datas do painel saem do `formato.ts` (P-FMT-01): o formatador único e o
 * fuso da casa, e nenhum `toLocaleString` por conta própria aqui.
 */
const num = (v: number) => numero(v, 1, true);
const titulo = (t: readonly [string, string], quantos: number) => (quantos === 1 ? t[0] : t[1]);
const dias = (x: number) => plural(x, 'dia', 'dias');
/* «1 vence», «3 vencem»; «1 nem começou», «2 nem começaram». */
const vencem = (x: number) => plural(x, 'vence', 'vencem');
const nemComecaram = (x: number) => plural(x, 'nem começou', 'nem começaram');

/** Os parágrafos do «i», e o que vier depois deles (uma lista). */
function explicar(textos: readonly string[], depois?: ReactNode, trocas: Record<string, string> = {}): ReactNode {
  const troca = (t: string) => t.replace(/\{(\w+)\}/g, (_, k: string) => trocas[k] ?? `{${k}}`);
  return h(Fragment, null,
    ...textos.map((t, i) => h('p', { key: `p${i}` }, troca(t))),
    depois ?? null);
}

/** Um cartão da tabela 5.1, com os textos da chave (ou o rótulo dela). */
function cartao(chave: string, numero: number, extra: {
  linha?: Linha; detalhe?: string | null; parado?: number | null; titulo?: string;
  texto?: TextoDoCartao; aVencer?: boolean; explicacao?: ReactNode;
}): Pendencia {
  const t = extra.texto ?? TEXTOS_DO_PAINEL[chave];
  const linha = extra.linha ?? LINHAS[chave];
  const nome = extra.titulo
    ?? (t ? titulo(extra.aVencer && t.tituloAVencer ? t.tituloAVencer : t.titulo, numero)
      : rotulo(chave.slice(chave.indexOf('.') + 1)));
  return {
    chave,
    numero,
    titulo: nome,
    consequencia: t?.consequencia ?? '',
    detalhe: extra.detalhe ?? undefined,
    gravidade: extra.aVencer ? 'saber' : linha.gravidade,
    area: linha.area,
    grupo: linha.grupo,
    acao: linha.acao,
    explicacao: extra.explicacao ?? (t ? explicar(t.explicacao) : undefined),
    permissao: linha.permissao || undefined,
    parado: extra.parado ?? null,
  };
}

/** O nome do ensaio de cada tipo de linha de cápsula (o mesmo da Estufa). */
const ENSAIO_DA_CAPSULA: Record<string, string> = {
  umidade: 'Teor de umidade',
  'limite-liquidez': 'Limite de liquidez',
  'limite-plasticidade': 'Limite de plasticidade',
  compactacao: 'Compactação',
  adensamento: 'Adensamento',
  'cisalhamento-direto': 'Cisalhamento direto',
  triaxial: 'Triaxial',
  granulometria: 'Granulometria',
  'massa-especifica': 'Massa específica',
};

const horasDesde = (v: string) => Math.floor((Date.now() - Date.parse(v)) / 3600e3);
/** «1 d 6 h», «30 h» — há quanto tempo a mais antiga entrou. */
function haQuanto(v: string): string {
  const x = horasDesde(v);
  if (!Number.isFinite(x) || x < 0) return '—';
  return x < 48 ? `${x} h` : `${Math.floor(x / 24)} d ${x % 24} h`;
}

/** As travas cujo exemplo é uma amostra (a API manda o nome do cliente junto). */
const TRAVAS_DE_AMOSTRA = new Set(['ensaio_sem_verificador', 'amostra_sem_os', 'amostra_a_guardar']);

const exemploDeAmostra = (codigo: string | null, identificacao?: string | null) => (
  codigo ? `ex.: ${nomeDaAmostra({ codigo, identificacao })}` : null);

// ── Os cartões (tabela 5.1) ──────────────────────────────────────────

/**
 * `GET /painel` → os cartões do Hoje.
 *
 * Situação com zero não vira cartão (o molde também não mostraria). O
 * número de cada cartão é o da API, sem conta nenhuma — a única soma é a da
 * tabela: os boletins parados (a conferir + a aprovar + aprovados sem
 * emitir) e as OS com ensaio que passou da data (uma por linha da API).
 */
export function pendenciasDoLaboratorio(d: DadosDoPainel): Pendencia[] {
  const saida: Pendencia[] = [];

  for (const t of d.travado ?? []) {
    const q = n(t.quantos);
    if (q <= 0) continue;
    const chave = `travado.${t.chave}`;
    const conhecida = !!LINHAS[chave];
    const deAmostra = TRAVAS_DE_AMOSTRA.has(t.chave) || !!t.exemplo_identificacao;
    saida.push(cartao(chave, q, {
      linha: conhecida ? undefined : DESCONHECIDA.travado,
      detalhe: !t.exemplo ? null
        : deAmostra ? exemploDeAmostra(t.exemplo, t.exemplo_identificacao)
          : t.chave === 'os_incompleta' ? `ex.: OS ${t.exemplo}`
            : t.chave === 'constante_nao_publicada' ? `certificado ${t.exemplo}`
              : `ex.: ${t.exemplo}`,
    }));
  }

  if (d.estufa && n(d.estufa.capsulas) > 0) {
    const e = d.estufa;
    const horas = num(n(e.horas));
    const ensaios = e.ensaios ?? [];
    const maisAntiga = ensaios.reduce<Esquecido | null>((a, x) => (!a || x.desde < a.desde ? x : a), null);
    const mostradas = ensaios.reduce((s, x) => s + n(x.capsulas), 0);
    const lista = ensaios.length > 0 ? h('ul', { key: 'lista' }, ...ensaios.map((x) => h('li', { key: x.ensaio_id },
      h(Link, { to: `/ensaio/${x.ensaio_id}/${x.tipo}` }, ENSAIO_DA_CAPSULA[x.tipo] ?? x.sigla),
      ` ${x.sigla} · ${nomeDaAmostra({ codigo: x.amostra_codigo, identificacao: x.amostra_identificacao, os: x.os_numero })}`
      + ` · cápsulas ${x.lista} · há ${haQuanto(x.desde)} · entrou ${diaEHoraCurtos(x.desde)}`))) : null;
    const nota = n(e.capsulas) > mostradas
      ? h('p', { key: 'nota' }, 'Aqui estão os doze ensaios mais antigos; a Estufa tem todos.') : null;
    saida.push(cartao('estufa', n(e.capsulas), {
      titulo: `${titulo(TEXTOS_DO_PAINEL.estufa.titulo, n(e.capsulas))} há mais de ${horas} h`,
      detalhe: maisAntiga
        ? `a mais antiga: ${nomeDaAmostra({ codigo: maisAntiga.amostra_codigo, identificacao: maisAntiga.amostra_identificacao })}`
          + ` · há ${haQuanto(maisAntiga.desde)}`
        : null,
      parado: maisAntiga && horasDesde(maisAntiga.desde) >= 0 ? Math.floor(horasDesde(maisAntiga.desde) / 24) : null,
      explicacao: explicar(TEXTOS_DO_PAINEL.estufa.explicacao, h(Fragment, null, lista, nota), { horas }),
    }));
  }

  for (const p of d.preso ?? []) {
    const q = n(p.ensaios);
    if (q <= 0) continue;
    const parado = p.dias_do_mais_parado === null ? null : n(p.dias_do_mais_parado);
    saida.push(cartao(`preso.${p.pendencia}`, q, {
      linha: LINHAS.preso,
      texto: TEXTOS_DO_PAINEL.preso,
      titulo: p.texto || titulo(TEXTOS_DO_PAINEL.preso.titulo, q),
      detalhe: [`em ${plural(n(p.ordens), 'OS', 'OS')}`,
        parado === null ? null : `mais parado há ${dias(parado)}`,
        /* A visão só traz o código da amostra (sem o nome do cliente). */
        exemploDeAmostra(p.exemplo)].filter(Boolean).join(' · '),
      parado,
    }));
  }

  for (const e of d.esperando ?? []) {
    const q = n(e.quantos);
    if (q <= 0) continue;
    const chave = `esperando.${e.chave}`;
    const parado = e.dias === null || e.dias === undefined ? null : n(e.dias);
    saida.push(cartao(chave, q, {
      linha: LINHAS[chave] ? undefined : DESCONHECIDA.esperando,
      detalhe: parado === null ? null : `mais parado há ${dias(parado)}`,
      parado,
    }));
  }

  const atrasados = (d.atrasados ?? []).filter((a) => n(a.ensaios) > 0);
  const noTeto = (d.atrasados ?? []).length >= TETO_DOS_ATRASADOS;
  if (atrasados.length > 0) {
    const pior = atrasados.reduce((a, x) => (n(x.pior_atraso) > n(a.pior_atraso) ? x : a));
    const ensaios = atrasados.reduce((s, x) => s + n(x.ensaios), 0);
    const nem = atrasados.reduce((s, x) => s + n(x.nem_comecaram), 0);
    const lista = h('ul', { key: 'lista' }, ...atrasados.map((a) => h('li', { key: a.os_numero },
      h(Link, { to: `/os?busca=${a.os_numero}` }, `OS ${a.os_numero}`),
      ` · ${plural(n(a.ensaios), 'ensaio', 'ensaios')}`
      + (n(a.nem_comecaram) > 0 ? ` · ${nemComecaram(n(a.nem_comecaram))}` : '')
      + ` · pior atraso: ${dias(n(a.pior_atraso))}`
      + (a.exemplo ? ` · ex.: ${nomeDaAmostra({ codigo: a.exemplo, identificacao: a.exemplo_identificacao })}` : ''))));
    saida.push(cartao('atrasados', atrasados.length, {
      detalhe: [noTeto ? `as ${TETO_DOS_ATRASADOS} com pior atraso; pode haver mais` : null,
        `pior atraso: ${dias(n(pior.pior_atraso))}, na OS ${pior.os_numero}`,
        plural(ensaios, 'ensaio', 'ensaios'),
        nem > 0 ? nemComecaram(nem) : null].filter(Boolean).join(' · '),
      parado: n(pior.pior_atraso),
      explicacao: explicar(
        [...TEXTOS_DO_PAINEL.atrasados.explicacao, ...(noTeto ? TEXTOS_DO_PAINEL.atrasados.noTeto ?? [] : [])],
        lista, { teto: String(TETO_DOS_ATRASADOS) }),
    }));
  }

  if (d.prazo && n(d.prazo.atrasadas) > 0) {
    const p = d.prazo;
    const pct = n(p.entregues) > 0 ? Math.round((n(p.no_prazo) / n(p.entregues)) * 100) : null;
    const resumo = [`${plural(n(p.abertas), 'OS aberta', 'OS abertas')}`,
      `${vencem(n(p.vencendo))} em até 7 dias`,
      n(p.sem_prazo) > 0 ? `${n(p.sem_prazo)} sem prazo contratual` : null,
      pct !== null ? `${pct}% entregues no prazo, de ${plural(n(p.entregues), 'concluída', 'concluídas')}` : null,
    ].filter(Boolean).join(' · ');
    saida.push(cartao('prazo.atrasadas', n(p.atrasadas), {
      detalhe: `pior atraso: ${dias(n(p.pior_atraso))}`
        + (n(p.vencendo) > 0 ? ` · ${vencem(n(p.vencendo))} em até 7 dias` : '')
        + (n(p.sem_prazo) > 0 ? ` · ${n(p.sem_prazo)} sem prazo` : ''),
      parado: n(p.pior_atraso),
      explicacao: explicar(TEXTOS_DO_PAINEL['prazo.atrasadas'].explicacao, h('p', { key: 'resumo' }, `Hoje: ${resumo}.`)),
    }));
  }

  for (const v of d.vencendo ?? []) {
    const chave = `vencendo.${v.chave}`;
    const conhecida = !!LINHAS[chave];
    const linha = conhecida ? undefined : DESCONHECIDA.vencendo;
    if (n(v.vencidos) > 0) {
      saida.push(cartao(chave, n(v.vencidos), {
        linha,
        detalhe: n(v.a_vencer) > 0 ? `e ${vencem(n(v.a_vencer))} em 30 dias` : null,
      }));
    }
    if (n(v.a_vencer) > 0) {
      const t = TEXTOS_DO_PAINEL[chave];
      saida.push(cartao(`${chave}.a_vencer`, n(v.a_vencer), {
        linha: linha ?? LINHAS[chave],
        texto: t,
        aVencer: true,
        titulo: t ? undefined : `${rotulo(v.chave)} a vencer`,
        detalhe: v.proximo ? `a próxima em ${data(v.proximo)}` : null,
      }));
    }
  }

  const m = d.medicao;
  if (m) {
    if (n(m.itens_nao_medidos) > 0) {
      saida.push(cartao('medicao.itens_nao_medidos', n(m.itens_nao_medidos), {
        detalhe: [n(m.os_com_nao_medido) > 0 ? `em ${plural(n(m.os_com_nao_medido), 'OS', 'OS')}` : null,
          n(m.itens_com_saldo) > 0 ? `${n(m.itens_com_saldo)} com saldo contratado` : null,
        ].filter(Boolean).join(' · ') || null,
      }));
    }
    const parados = n(m.aguardando_conferencia) + n(m.aguardando_aprovacao) + n(m.aprovadas_sem_emitir);
    if (parados > 0) {
      /* Os devolvidos são parte dos em elaboração (0132): entre parênteses
         junto deles; sozinhos, se a API um dia os mandar sem os outros. */
      const devolvidos = n(m.devolvidas) > 0 ? plural(n(m.devolvidas), 'devolvido', 'devolvidos') : null;
      const elaboracao = n(m.em_elaboracao) > 0
        ? `${n(m.em_elaboracao)} em elaboração${devolvidos ? ` (${devolvidos})` : ''}`
        : devolvidos;
      saida.push(cartao('medicao.boletins_parados', parados, {
        detalhe: [`${n(m.aguardando_conferencia)} a conferir`, `${n(m.aguardando_aprovacao)} a aprovar`,
          plural(n(m.aprovadas_sem_emitir), 'aprovado sem emitir', 'aprovados sem emitir'),
          elaboracao].filter(Boolean).join(' · '),
      }));
    }
    if (n(m.contestadas) > 0) saida.push(cartao('medicao.contestadas', n(m.contestadas), {}));
    if (n(m.itens_excedentes) > 0) saida.push(cartao('medicao.itens_excedentes', n(m.itens_excedentes), {}));
    if (n(m.os_sem_escopo) > 0) saida.push(cartao('medicao.os_sem_escopo', n(m.os_sem_escopo), {}));
  }

  /* As áreas na ordem fixa (o molde mostra os chips na ordem em que os
     cartões chegam); a ordem da pilha o molde decide. */
  const peso = (a: string) => {
    const i = ORDEM_DAS_AREAS.indexOf(a);
    return i < 0 ? ORDEM_DAS_AREAS.length : i;
  };
  return saida
    .map((p, i) => ({ p, i }))
    .sort((a, b) => peso(a.p.area) - peso(b.p.area) || a.i - b.i)
    .map(({ p }) => p);
}

// ── Os indicadores (tabela 5.2) ──────────────────────────────────────

const nulo = (v: string | number | null | undefined) => v === null || v === undefined || v === '';

/**
 * `GET /painel/laboratorio` → os quatro números do Andamento, cada um com a
 * comparação ao período anterior escrita em palavra (o tom só reforça) e,
 * quando há `semanas` (M4), a série de 8 semanas do minigráfico.
 */
export function indicadoresDoLaboratorio(l: DadosLab, semanas?: Semana[]): Indicador[] {
  const s = semanas ?? [];
  const serie = (f: (x: Semana) => number) => (s.length >= 2 ? s.map(f) : undefined);

  /* A fila de ensaios: o que entrou menos o que saiu com laudo. Parada, a
     frase fala do período escolhido: «semana» só quando ele é de 7 dias. */
  const f = l.fila;
  const saldo = n(f.entraram) - n(f.sairam);
  const antes = n(f.entraram_antes) - n(f.sairam_antes);
  const umaSemana = n(l.periodo?.dias) > 0 && n(l.periodo?.dias) <= 7;
  const fila: Indicador = {
    chave: 'fila',
    rotulo: 'A fila de ensaios',
    valor: sinal(saldo, 0),
    unidade: pluralSemNumero(saldo, 'ensaio', 'ensaios'),
    detalhe: n(f.entraram) === 0 && n(f.sairam) === 0
      ? 'Nenhum ensaio entrou e nenhum saiu com laudo no período. '
        + (umaSemana ? 'Em semana de campo parada isso é normal; em semana de entrega, não.'
          : 'Com o campo parado isso é normal; com entrega marcada, não.')
      : `entraram ${n(f.entraram)} · saíram ${n(f.sairam)} com laudo`,
    comparacao: saldo > antes ? { texto: `cresceu (antes, ${sinal(antes, 0)})`, tom: 'ruim' }
      : saldo < antes ? { texto: `diminuiu (antes, ${sinal(antes, 0)})`, tom: 'bom' }
        : { texto: 'igual ao período anterior', tom: 'neutro' },
    serie: serie((x) => n(x.entraram) - n(x.sairam)),
  };

  /* Do ensaio ao laudo (L35): a mediana do período; a comparação é a média. */
  const l35 = l.prazo.l35;
  const semLaudo = nulo(l35.mediana) || n(l35.n) === 0;
  const media = nulo(l35.media) ? null : Number(l35.media);
  const mediaAntes = nulo(l35.media_antes) ? null : Number(l35.media_antes);
  const dif = media !== null && mediaAntes !== null ? Math.round((media - mediaAntes) * 10) / 10 : null;
  const diaOuDias = (v: number) => `${num(v)} ${pluralSemNumero(v, 'dia', 'dias')}`;
  const laudo: Indicador = {
    chave: 'laudo',
    rotulo: 'Do ensaio ao laudo',
    valor: semLaudo ? '—' : num(Number(l35.mediana)),
    unidade: semLaudo ? undefined : pluralSemNumero(Number(l35.mediana), 'dia', 'dias'),
    detalhe: semLaudo ? 'nenhum laudo no período'
      : `mediana de ${plural(n(l35.n), 'laudo', 'laudos')}${media === null ? '' : ` · média ${diaOuDias(media)}`}`,
    comparacao: semLaudo || dif === null ? undefined
      : dif < 0 ? { texto: `${diaOuDias(-dif)} a menos na média`, tom: 'bom' }
        : dif > 0 ? { texto: `${diaOuDias(dif)} a mais na média`, tom: 'ruim' }
          : { texto: 'igual ao período anterior', tom: 'neutro' },
    serie: serie((x) => (x.mediana_laudo === null || x.mediana_laudo === undefined ? NaN : Number(x.mediana_laudo))),
  };

  /* Terminados na data programada (a meta é a primeira programação, D4). */
  const m = l.prazo.meta;
  const pct = n(m.terminados) > 0 ? Math.round((n(m.na_meta) / n(m.terminados)) * 100) : null;
  const pctAntes = n(m.terminados_antes) > 0 ? Math.round((n(m.na_meta_antes) / n(m.terminados_antes)) * 100) : null;
  const pontos = pct !== null && pctAntes !== null ? pct - pctAntes : null;
  const pts = (v: number) => plural(v, 'ponto', 'pontos');
  const meta: Indicador = {
    chave: 'meta',
    rotulo: 'Terminados na data programada',
    valor: pct === null ? '—' : String(pct),
    unidade: pct === null ? undefined : '%',
    detalhe: n(m.terminados) > 0
      ? `${n(m.na_meta)} de ${plural(n(m.terminados), 'terminado', 'terminados')}`
      : 'nenhum ensaio programado terminou no período',
    comparacao: pontos === null ? undefined
      : pontos > 0 ? { texto: `${pts(pontos)} a mais`, tom: 'bom' }
        : pontos < 0 ? { texto: `${pts(-pontos)} a menos`, tom: 'ruim' }
          : { texto: 'igual ao período anterior', tom: 'neutro' },
    serie: serie((x) => (n(x.terminados) > 0 ? Math.round((n(x.na_meta) / n(x.terminados)) * 100) : NaN)),
  };

  /* Amostras a chegar: é previsão, não tem período anterior nem série. */
  const c = l.entrada.aChegar;
  const chegar: Indicador = {
    chave: 'chegar',
    rotulo: 'Amostras a chegar',
    valor: String(n(c.amostras)),
    unidade: n(c.amostras) === 1 ? 'amostra' : 'amostras',
    detalhe: `${n(c.proximos_7)} nos próximos 7 dias · ${n(c.sem_previsao)} sem previsão`,
    comparacao: n(c.atrasadas) > 0
      ? { texto: `${n(c.atrasadas)} com chegada atrasada`, tom: 'ruim' } : undefined,
  };

  return [fila, laudo, meta, chegar];
}
